import React, { useEffect, useState, useCallback } from "react";
import "./attendance.css";
import "../AddWorkReport/work_report.css";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Target,
  LogIn,
  LogOut,
  AlertCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  addAttendanceLog,
  getMyAttendanceLogs,
  addTodayAttendance,
  getCompanyHolidays,
} from "../../service/attendance_service";
import { getMyLeaves } from "../../service/project_service";

const Attendance = () => {
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState("--:--");
  const [checkOutTime, setCheckOutTime] = useState("--:--");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [isRestrictedDay, setIsRestrictedDay] = useState(false);
  const [restrictionReason, setRestrictionReason] = useState("");
  const [attendanceData, setAttendanceData] = useState({});
  const [stats, setStats] = useState({
    present: 0,
    absent: 0,
    workingHours: 0,
    punctuality: 0,
  });

  const getDaysInMonth = (year, month) =>
    new Date(year, month + 1, 0).getDate();
  const getStartDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === "--:--") return "--:--";
    return timeStr.replace(":", ".");
  };

  const getHHMMAsNumber = (timeStr) => {
    if (!timeStr || timeStr === "--:--") return 0;
    const formatted = formatTime(timeStr);
    return parseFloat(formatted);
  };

  const calculateWorkingHours = (inTimeStr, outTimeStr) => {
    if (
      !inTimeStr ||
      inTimeStr === "--:--" ||
      !outTimeStr ||
      outTimeStr === "--:--"
    )
      return 0;

    const [inH, inM] = inTimeStr.replace(":", ".").split(".").map(Number);
    const [outH, outM] = outTimeStr.replace(":", ".").split(".").map(Number);

    const inTotalMins = inH * 60 + inM;
    const outTotalMins = outH * 60 + outM;

    if (outTotalMins <= inTotalMins) return 0;

    const diffMins = outTotalMins - inTotalMins;
    const h = Math.floor(diffMins / 60);
    const m = diffMins % 60;

    return parseFloat(`${h}.${m.toString().padStart(2, "0")}`);
  };

  const checkRestriction = useCallback((holidaysList, leavesList = [], todayLogStatus = "") => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday
    const date = today.getDate();

    // 1. Sunday check
    if (dayOfWeek === 0) {
      setIsRestrictedDay(true);
      setRestrictionReason("Today is Sunday (Weekly Off)");
      return;
    }

    // 2. 2nd and 4th Saturday check
    if (dayOfWeek === 6) {
      if ((date > 7 && date <= 14) || (date > 21 && date <= 28)) {
        setIsRestrictedDay(true);
        setRestrictionReason(
          "Today is a Restricted Saturday (2nd/4th Saturday Off)",
        );
        return;
      }
    }

    // 3. Holiday check
    const todayStr = today.toISOString().split("T")[0];
    const isHoliday = holidaysList.some((h) => h.date?.startsWith(todayStr));

    if (isHoliday) {
      const holiday = holidaysList.find((h) => h.date?.startsWith(todayStr));
      setIsRestrictedDay(true);
      setRestrictionReason(`Today is a Public Holiday: ${holiday.title}`);
      return;
    }

    // 4. Attendance Status check
    if (todayLogStatus === "Absent") {
      setIsRestrictedDay(true);
      setRestrictionReason("Your attendance status for today is marked as Absent.");
      return;
    }

    // 5. Leave check
    const todayLeave = leavesList.find(l =>
      l.status === "Approved" &&
      todayStr >= l.startdate &&
      todayStr <= l.enddate
    );

    if (todayLeave) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTimeFloat = currentHour + currentMinute / 60;

      if (todayLeave.duration === "Full Day") {
        setIsRestrictedDay(true);
        setRestrictionReason(`Today is a Leave Day: ${todayLeave.leavetype}`);
        return;
      } else if (todayLeave.duration === "Half Day - Morning") {
        if (currentTimeFloat >= 9.5 && currentTimeFloat < 14.0) { // 9:30 AM to 2:00 PM
          setIsRestrictedDay(true);
          setRestrictionReason(`Morning half-day leave: Check-in and check-out are restricted between 9:30 AM and 2:00 PM.`);
          return;
        }
        setRestrictionReason(`Today is a Morning Leave: ${todayLeave.leavetype}.`);
      } else if (todayLeave.duration === "Half Day - Afternoon") {
        if (currentTimeFloat >= 14.0 && currentTimeFloat < 18.5) { // 2:00 PM to 6:30 PM
          setIsRestrictedDay(true);
          setRestrictionReason(`Afternoon half-day leave: Check-in and check-out are restricted between 2:00 PM and 6:30 PM.`);
          return;
        }
        setRestrictionReason(`Today is an Afternoon Leave: ${todayLeave.leavetype}.`);
      }
    }

    setIsRestrictedDay(false);
    setRestrictionReason("");
  }, []);

  const fetchAttendanceLogs = useCallback(async () => {
    try {
      const [logsRes, holidayRes, leavesRes] = await Promise.allSettled([
        getMyAttendanceLogs(),
        getCompanyHolidays(),
        getMyLeaves(),
      ]);

      let leavesList = [];
      if (leavesRes.status === "fulfilled" && leavesRes.value.status === "success") {
        leavesList = leavesRes.value.data.leaves || [];
      }

      let holidaysList = [];
      if (
        holidayRes.status === "fulfilled" &&
        holidayRes.value.status === "success"
      ) {
        holidaysList = holidayRes.value.data || [];
        setHolidays(holidaysList);
      }

      let currentTodayStatus = "";

      if (
        logsRes.status === "fulfilled" &&
        logsRes.value.status === "success"
      ) {
        const response = logsRes.value;
        const dataMap = {};
        let presentCount = 0;
        let absentCount = 0;
        let totalHours = 0;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let foundToday = false;

        response.data.forEach((log) => {
          const logDate = new Date(log.date);
          const logDateOnly = new Date(log.date);
          logDateOnly.setHours(0, 0, 0, 0);

          if (
            logDate.getMonth() === currentDate.getMonth() &&
            logDate.getFullYear() === currentDate.getFullYear()
          ) {
            dataMap[logDate.getDate()] = log.status.toLowerCase();
            if (log.status === "Present" || log.status.includes("Half Day")) {
              if (log.status.includes("Half Day")) presentCount += 0.5;
              else presentCount++;
            }
            if (log.status === "Absent") absentCount++;
            if (log.workinghours) totalHours += parseFloat(log.workinghours);
          }

          if (logDateOnly.getTime() === today.getTime()) {
            foundToday = true;
            currentTodayStatus = log.status;

            if (log.checkin && parseFloat(log.checkin) !== 0) {
              const checkInStr = log.checkin.toString();
              setCheckInTime(checkInStr);
              localStorage.setItem("checkIn", checkInStr);

              if (log.checkout && parseFloat(log.checkout) !== 0) {
                setCheckOutTime(log.checkout.toString());
                setCheckedIn(false);
              } else {
                setCheckOutTime("--:--");
                setCheckedIn(true);
              }
            } else {
              setCheckInTime("--:--");
              setCheckOutTime("--:--");
              setCheckedIn(false);
              localStorage.removeItem("checkIn");
            }
          }
        });

        if (!foundToday) {
          setCheckInTime("--:--");
          setCheckOutTime("--:--");
          setCheckedIn(false);
          localStorage.removeItem("checkIn");
        }

        setAttendanceData(dataMap);
        const totalTrackedDays = presentCount + absentCount;
        setStats({
          present: presentCount,
          absent: absentCount,
          workingHours: totalHours.toFixed(2),
          punctuality:
            totalTrackedDays > 0
              ? ((presentCount / totalTrackedDays) * 100).toFixed(1)
              : "0",
        });
      }

      checkRestriction(holidaysList, leavesList, currentTodayStatus);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  }, [currentDate, checkRestriction]);

  useEffect(() => {
    fetchAttendanceLogs();
  }, [fetchAttendanceLogs]);

  const generateCalendarDays = () => {
    const days = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const startDay = getStartDayOfMonth(year, month);

    const today = new Date();
    const isCurrentMonth =
      today.getFullYear() === year && today.getMonth() === month;

    for (let i = 1; i <= daysInMonth; i++) {
      const dayOfWeek = (i + startDay - 1) % 7;
      const weekNumber = Math.ceil((i + startDay) / 7);

      let status = attendanceData[i];
      const isSunday = dayOfWeek === 0;
      const isSatOff =
        dayOfWeek === 6 && (weekNumber === 2 || weekNumber === 4);

      // Check if this date is a holiday
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
      const isHoliday = holidays.some((h) => h.date?.startsWith(dateStr));

      const isOffDay = isSunday || isSatOff || isHoliday;
      const isToday = isCurrentMonth && today.getDate() === i;

      days.push(
        <div
          key={`${year}-${month}-${i}`}
          className={`cal-date-cell ${isToday ? "today" : ""} ${isOffDay ? "off-day" : status || ""}`}
        >
          {i}
          {!isOffDay && (
            <>
              {status === "present" && (
                <span className="cal-mark present" title="Present"></span>
              )}
              {status === "absent" && (
                <span className="cal-mark absent" title="Absent"></span>
              )}
              {status === "half day - morning" && (
                <span className="cal-mark half-morning" title="Half Day - Morning" style={{ background: "#8be9fd" }}></span>
              )}
              {status === "half day - afternoon" && (
                <span className="cal-mark half-afternoon" title="Half Day - Afternoon" style={{ background: "#ffb86c" }}></span>
              )}
            </>
          )}
          {isHoliday && (
            <span
              className="cal-mark holiday"
              style={{ background: "#f59e0b" }}
              title="Holiday"
            ></span>
          )}
        </div>,
      );
    }
    return days;
  };

  const generateEmptyCells = () => {
    const startDay = getStartDayOfMonth(
      currentDate.getFullYear(),
      currentDate.getMonth(),
    );
    return [...Array(startDay)].map((_, i) => (
      <div
        key={`empty-${i}`}
        className="cal-date-cell empty-cell"
        style={{ visibility: "hidden" }}
      ></div>
    ));
  };

  const handleCheckIn = async () => {
    if (isRestrictedDay) return;
    const timeStr = new Date().toTimeString().slice(0, 5).replace(":", ".");
    setSubmitLoading(true);
    try {
      const checkInValue = getHHMMAsNumber(timeStr);
      await addTodayAttendance({ checkin: checkInValue });
      toast.success("Check-in successful");
      setCheckInTime(timeStr);
      localStorage.setItem("checkIn", timeStr);
      setCheckedIn(true);
      fetchAttendanceLogs();
    } catch (error) {
      toast.error(error.message || "Failed to check in");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (isRestrictedDay) return;
    const outTime = new Date().toTimeString().slice(0, 5).replace(":", ".");
    setSubmitLoading(true);
    try {
      const checkInValue = getHHMMAsNumber(checkInTime);
      const checkOutValue = getHHMMAsNumber(outTime);
      const workingHours = calculateWorkingHours(checkInTime, outTime);

      const response = await addAttendanceLog({
        checkin: checkInValue,
        checkout: checkOutValue,
        workinghours: workingHours,
      });

      if (response.status === "success") {
        toast.success("Check-out successful");
        setCheckOutTime(outTime);
        localStorage.removeItem("checkIn");
        setCheckedIn(false);
        setCheckInTime("--:--");
        setCheckOutTime("--:--");
        fetchAttendanceLogs();
      } else {
        toast.error(response.message || "Failed to add attendance");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="add-report-container">
      <div className="page-top-header">
        <h4>Attendance Dashboard</h4>
      </div>
      <div className="report-detail-grid-new">
        {isRestrictedDay && (
          <div
            style={{
              background: "#fff1f2",
              border: "1px solid #fecaca",
              padding: "16px",
              borderRadius: "12px",
              color: "#b91c1c",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              gridColumn: "1 / -1",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <AlertCircle size={20} />
            {restrictionReason}
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <div className="loader"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            <div className="check-in-grid">
              <div
                className={`attendance-status-card ${checkedIn ? "active-session" : ""}`}
              >
                <div className="card-indicator"></div>
                <div className="status-header">
                  <div className="status-icon-box in">
                    <LogIn size={22} strokeWidth={2.5} />
                  </div>
                  <div className="status-badge pulse">
                    {checkedIn ? "Session Active" : "Waiting for Check-in"}
                  </div>
                </div>
                <div className="status-content">
                  <p className="status-label">Check-in Time</p>
                  <h4 className="status-time">{formatTime(checkInTime)}</h4>
                </div>
                <button
                  className="modern-action-btn in-btn"
                  onClick={handleCheckIn}
                  disabled={
                    checkedIn ||
                    submitLoading ||
                    isRestrictedDay ||
                    (checkInTime !== "--:--" && checkOutTime !== "--:--")
                  }
                  style={{
                    opacity:
                      checkedIn ||
                        submitLoading ||
                        isRestrictedDay ||
                        (checkInTime !== "--:--" && checkOutTime !== "--:--")
                        ? 0.6
                        : 1,
                    cursor:
                      checkedIn ||
                        submitLoading ||
                        isRestrictedDay ||
                        (checkInTime !== "--:--" && checkOutTime !== "--:--")
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <div className="btn-effect"></div>
                  <span>
                    {submitLoading
                      ? "Processing..."
                      : checkedIn
                        ? "CHECKED IN"
                        : "CHECK IN"}
                  </span>
                </button>
              </div>

              <div className="attendance-status-card checkout-card">
                <div className="card-indicator"></div>
                <div className="status-header">
                  <div className="status-icon-box out">
                    <LogOut size={22} strokeWidth={2.5} />
                  </div>
                  {checkOutTime !== "--:--" && !checkedIn && (
                    <div className="status-badge success">CHECK OUT</div>
                  )}
                </div>
                <div className="status-content">
                  <p className="status-label">Check-out Time</p>
                  <h4 className="status-time">{formatTime(checkOutTime)}</h4>
                </div>
                <button
                  className="modern-action-btn out-btn"
                  onClick={handleCheckOut}
                  style={{
                    opacity:
                      !checkedIn ||
                        submitLoading ||
                        isRestrictedDay ||
                        (checkInTime !== "--:--" && checkOutTime !== "--:--")
                        ? 0.6
                        : 1,
                    cursor:
                      !checkedIn ||
                        submitLoading ||
                        isRestrictedDay ||
                        (checkInTime !== "--:--" && checkOutTime !== "--:--")
                        ? "not-allowed"
                        : "pointer",
                  }}
                  disabled={
                    !checkedIn ||
                    submitLoading ||
                    isRestrictedDay ||
                    (checkInTime !== "--:--" && checkOutTime !== "--:--")
                  }
                >
                  <div className="btn-effect"></div>
                  <span>{submitLoading ? "Processing..." : "CHECK OUT"}</span>
                </button>
              </div>
            </div>

            <div className="calendar-insights-wrapper">
              <div className="calendar-container-card" style={{ margin: 0 }}>
                <div className="cal-header">
                  <div className="cal-title-wrap">
                    <div
                      className="header-icon-box"
                      style={{
                        background: "#f0f9ff",
                        padding: "8px",
                        borderRadius: "10px",
                      }}
                    >
                      <CalendarDays size={24} color="#0ea5e9" />
                    </div>
                    <div>
                      <h3 style={{ fontSize: "18px", fontWeight: "700" }}>
                        Attendance Calendar
                      </h3>
                      <p
                        style={{ fontSize: "12px", color: "#64748b", margin: 0 }}
                      >
                        Showing data for{" "}
                        {currentDate.toLocaleString("default", {
                          month: "long",
                        })}{" "}
                        {currentDate.getFullYear()}
                      </p>
                    </div>
                  </div>
                  <div className="calendar-controls">
                    <button className="nav-btn" onClick={handlePrevMonth}>
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      className="nav-btn"
                      style={{
                        fontSize: "12px",
                        width: "auto",
                        padding: "0 15px",
                        fontWeight: "700",
                      }}
                    >
                      {currentDate.toLocaleString("default", {
                        month: "short",
                      })}{" "}
                      {currentDate.getFullYear()}
                    </button>
                    <button className="nav-btn" onClick={handleNextMonth}>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <div className="cal-grid-layout">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day) => (
                      <div key={day} className="cal-day-label">
                        {day}
                      </div>
                    ),
                  )}

                  {generateEmptyCells()}
                  {generateCalendarDays()}
                </div>

                <div className="cal-legend-row" style={{ flexWrap: "wrap" }}>
                  <div className="cal-legend-item">
                    <div
                      className="cal-legend-dot"
                      style={{ background: "#10b981" }}
                    ></div>
                    Present
                  </div>
                  <div className="cal-legend-item">
                    <div
                      className="cal-legend-dot"
                      style={{ background: "#ef4444" }}
                    ></div>
                    Absent
                  </div>
                  <div className="cal-legend-item">
                    <div
                      className="cal-legend-dot"
                      style={{ background: "#8be9fd" }}
                    ></div>
                    Half Day - Morning
                  </div>
                  <div className="cal-legend-item">
                    <div
                      className="cal-legend-dot"
                      style={{ background: "#ffb86c" }}
                    ></div>
                    Half Day - Afternoon
                  </div>
                  <div className="cal-legend-item">
                    <div
                      className="cal-legend-dot"
                      style={{ background: "#f59e0b" }}
                    ></div>
                    Holiday
                  </div>
                  <div className="cal-legend-item">
                    <div
                      className="cal-legend-dot"
                      style={{ background: "#f1f5f9", border: "1px solid #e2e8f0" }}
                    ></div>
                    Off-Day
                  </div>
                </div>
              </div>

              <div
                className="sidebar-info-card"
                style={{
                  borderTop: "4px solid #0ea5e9",
                  margin: 0,
                  height: "100%",
                }}
              >
                <h5 style={{ border: "none", padding: 0 }}>
                  <Target size={18} color="#0ea5e9" />
                  Monthly Insights
                </h5>
                <div style={{ marginTop: "5px" }}>
                  <div className="sidebar-stats-item">
                    <span className="stats-label">Punctuality Score</span>
                    <span className="stats-value" style={{ color: "#10b981" }}>
                      {`${stats.punctuality}%`}
                    </span>
                  </div>
                  <div className="sidebar-stats-item">
                    <span className="stats-label">Working Hours</span>
                    <span className="stats-value">
                      {stats.workingHours} Hrs
                    </span>
                  </div>
                  <div className="sidebar-stats-item">
                    <span className="stats-label">Present Days</span>
                    <span className="stats-value">
                      {stats.present < 10 ? `0${stats.present}` : stats.present}
                    </span>
                  </div>
                  <div className="sidebar-stats-item">
                    <span className="stats-label">Absent Days</span>
                    <span className="stats-value">
                      {stats.absent < 10 ? `0${stats.absent}` : stats.absent}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <div style={{ height: "40px" }}></div>
      </div>
    </div>
  );
};

export default Attendance;
